import React, { Fragment, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { HostedForm, HostedFormDispatchDataResponse } from 'react-acceptjs'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputAdornment,
  InputLabel,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material'
import { HostedFormBankDataResponse, HostedPaymentFormProps } from '../../interfaceTypes'
import { AppContext } from '../../context'
import { ActionType } from '../../context/AppContextReducer'
import { cardPaymentSchema } from '../../validationSchema'
import { CustomModal } from '../common/CustomModal'
import palette from '../../theme/palette'
import { StyledAmountInput } from '../../theme/styleComponents'
import { Alert } from '../common/Alert'
import { isProductionMode } from '../../constants'
import { AccountType, BillingType, PaymentMethod, useRecordPaymentMutation } from '../../../generated'

const HostedPaymentForm: React.FC<HostedPaymentFormProps> = ({
  clientKey,
  apiLoginID,
  billAmount,
  totalPendingAmount,
  paymentMethod,
  billAccessToken,
  billingType,
  refetch
}) => {
  const { isOpen, dispatch: appDispatch } = useContext(AppContext)
  const [isProcessing, setIsProcessing] = useState(false)
  const paymentFormRef = useRef<HTMLFormElement>(null)

  const [recordPayment] = useRecordPaymentMutation({
    onError: error => {
      Alert.error(error.message || 'Failed to record payment')
    },
    onCompleted: data => {
      Alert.success('Payment recorded successfully')
      refetch()
    }
  })

  const methods = useForm<any>({
    // <<<--- NEED TO UPDATE TYPE
    mode: 'onSubmit',

    resolver: yupResolver(cardPaymentSchema),
    defaultValues: {
      cardNumber: '',
      expiryDate: '',
      cvv: '',
      cardHolderName: '',
      paymentChoice: 'billPayment',
      partialPayment: false,
      fullPayment: false,
      amount: billAmount
    }
  })

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
    setValue,
    watch
  } = methods

  const paymentChoice = watch('paymentChoice')
  const amount = watch('amount')
  const isPartialPayment = useMemo(() => paymentChoice === String('partialPayment'), [paymentChoice])
  const isFullPayment = useMemo(() => paymentChoice === String('fullPayment'), [paymentChoice])
  const isBillPayment = useMemo(() => paymentChoice === String('billPayment'), [paymentChoice])
  const isFlatFeeBill = billingType === BillingType.FlatFee
  const maxAmountLimit = isFlatFeeBill && isFullPayment ? totalPendingAmount : billAmount

  const handleClose = () => {
    if (!isProcessing) {
      if (paymentFormRef.current) {
        paymentFormRef.current.reset()
      }
      reset({})
      setIsProcessing(false)
      appDispatch({ type: ActionType.SET_IS_OPEN, isOpen: !isOpen })
    }
  }

  const handleRecordPayment = (data: HostedFormBankDataResponse & HostedFormDispatchDataResponse) => {
    const {
      encryptedBankData,
      encryptedCardData,
      customerInformation,
      opaqueData: { dataValue }
    } = data

    const token = dataValue
    const amountInput = Number(watch('amount'))
    const amount = amountInput > billAmount ? billAmount : amountInput
    const advancePaymentAmount = amountInput > billAmount ? amountInput - billAmount : 0

    recordPayment({
      variables: {
        recordPaymentInput: {
          accountType: AccountType.Operating,
          billAccessToken,
          amount,
          ...(advancePaymentAmount ? { advancePaymentAmount } : undefined),
          method: paymentMethod,
          bankAccountToken: paymentMethod === PaymentMethod.Ach ? token : undefined,
          cardToken: paymentMethod === PaymentMethod.Card ? token : undefined,
          cardData: encryptedCardData && {
            cardNumber: encryptedCardData.cardNumber,
            expDate: encryptedCardData.expDate,
            customerFirstName: customerInformation.firstName,
            customerLastName: customerInformation.lastName
          },
          bankData: encryptedBankData
        }
      }
    })
  }

  // github.com/brendanbond/react-acceptjs/issues/17
  const onSubmit = async (data: unknown) => {
    const _data = data as HostedFormBankDataResponse & HostedFormDispatchDataResponse
    const {
      messages: { resultCode }
    } = _data

    if (resultCode === 'Ok') {
      handleRecordPayment(_data)
      handleClose()
    } else {
      Alert.error('Payment failed. Please check the information and try again.')
    }
  }

  useEffect(() => {
    if (isPartialPayment) {
      setValue('amount', (billAmount / 2).toFixed(2))
    }

    if (isFullPayment) {
      setValue('amount', totalPendingAmount)
    }

    if (isBillPayment) {
      setValue('amount', billAmount)
    }
  }, [billAmount, isBillPayment, isFullPayment, isPartialPayment, setValue, totalPendingAmount])

  return (
    <CustomModal title="Pay Bill" isOpen={isOpen} handleClose={handleClose}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} ref={paymentFormRef}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  padding: '42px',
                  alignItems: 'center',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  background: palette.primary.main
                }}
              >
                <Typography variant="h6" sx={{ color: 'white' }}>
                  Pay
                </Typography>
                <Typography variant="h5" sx={{ color: 'white' }}>
                  ${amount}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={8}>
              <FormControl error={!!errors.accountType} variant="standard" fullWidth>
                <FormLabel id="paymentChoice">Payment Type</FormLabel>
                <Controller
                  name="paymentChoice"
                  control={control}
                  render={({ field: { onChange, value }, fieldState: { invalid, error: { message } = {} } }) => (
                    <RadioGroup row aria-label="accountType" name="accountType" value={value} onChange={onChange}>
                      <FormControlLabel value="billPayment" control={<Radio />} label="Bill Payment" />
                      {isFlatFeeBill && (
                        <FormControlLabel value="fullPayment" control={<Radio />} label="Advance Payment" />
                      )}
                      <FormControlLabel value="partialPayment" control={<Radio />} label="Partial Payment" />
                    </RadioGroup>
                  )}
                />
              </FormControl>

              <Fragment>
                <InputLabel id="amount">
                  {isPartialPayment ? 'Partial Amount' : isFullPayment ? 'Total Balance Amount' : 'Current Bill Amount'}
                </InputLabel>
                <Controller
                  name="amount"
                  control={control}
                  render={({ field, fieldState: { invalid, error: { message } = {} } }) => (
                    <StyledAmountInput
                      {...field}
                      disabled={isBillPayment}
                      type="number"
                      inputMode="decimal"
                      inputProps={{
                        pattern: '\\d+(\\.\\d{1,2})?'
                      }}
                      InputProps={{
                        inputProps: {
                          max: maxAmountLimit
                        },
                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                      }}
                      onChange={e => {
                        const value = Number(e.target.value)
                        if (value <= maxAmountLimit && value > 0) {
                          field.onChange(e)
                        }
                      }}
                      margin="dense"
                      fullWidth
                      variant="outlined"
                      error={invalid}
                      helperText={message}
                      placeholder="Amount"
                    />
                  )}
                />
              </Fragment>

              <HostedForm
                authData={{
                  apiLoginID: apiLoginID,
                  clientKey: clientKey
                }}
                onSubmit={onSubmit}
                paymentOptions={{
                  showCreditCard: paymentMethod === PaymentMethod.Card,
                  showBankAccount: paymentMethod === PaymentMethod.Ach
                }}
                environment={isProductionMode ? 'PRODUCTION' : 'SANDBOX'}
                buttonText="Pay"
                formHeaderText=""
                buttonStyle={{
                  backgroundColor: palette.navyBlue,
                  color: 'white',
                  padding: '12px 24px',
                  width: '100%',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontFamily: 'Roboto',
                  fontWeight: 500,
                  cursor: 'pointer',
                  marginTop: '20px'
                }}
              />
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </CustomModal>
  )
}

export default HostedPaymentForm
